import { useState } from 'react'
import { Avatar, Box, Button, Chip, Divider, Menu, Stack, Typography } from '@mui/material'
import { LogOut } from 'lucide-react'
import { useAuth, useStaffMe } from '@/auth'
import { isoDate } from '@/lib/format'

/**
 * Who is signed in, and the way out.
 *
 * ── THE APP HAD NEITHER ─────────────────────────────────────────────────────
 * Nothing outside `src/auth/` called `useAuth()`, `logout()`, `useStaffMe()` or
 * `useCan()`. `logout()` existed and was never invoked from anywhere, so a staff
 * member on a shared machine had no way to leave, and no screen said whose
 * numbers were on it.
 *
 * ── AND IT COSTS NO REQUEST ─────────────────────────────────────────────────
 * `useStaffMe()` is the same `['staff','me']` query key `StaffOnly` already
 * fires, with `staleTime: Infinity`, so TanStack serves this from cache —
 * `staffQueryGate.test.tsx`'s "exactly once after auth lands" still holds.
 *
 * The capabilities chips are the only place the documented write seam becomes
 * visible to a person: `capabilities` is a string array precisely so that
 * adding `calibration:write` is a server change and zero client refactor.
 */
export function AccountMenu() {
  const { logout } = useAuth()
  const { data } = useStaffMe()
  const [anchor, setAnchor] = useState<HTMLElement | null>(null)

  const name = data?.display_name ?? 'Cuenta'
  const initials = name
    .split(/\s+/)
    .slice(0, 2)
    .map((w) => w[0]?.toUpperCase() ?? '')
    .join('')

  return (
    <>
      <Button
        onClick={(e) => setAnchor(e.currentTarget)}
        aria-label={`Cuenta: ${name}`}
        aria-haspopup="menu"
        sx={{ color: 'inherit', textTransform: 'none', minWidth: 0, px: 0.75 }}
        startIcon={
          <Avatar sx={{ width: 22, height: 22, fontSize: '0.65rem', bgcolor: 'secondary.main' }}>
            {initials}
          </Avatar>
        }
      >
        <Box component="span" sx={{ display: { xs: 'none', md: 'inline' }, fontWeight: 500 }}>
          {name}
        </Box>
      </Button>

      <Menu anchorEl={anchor} open={Boolean(anchor)} onClose={() => setAnchor(null)}>
        <Stack spacing={1.25} sx={{ px: 2, py: 1.25, minWidth: 260 }}>
          <Box>
            <Typography variant="body2" sx={{ fontWeight: 600 }}>
              {name}
            </Typography>
            {data?.granted_at && (
              <Typography variant="caption" color="text.secondary">
                Acceso concedido el {isoDate(data.granted_at)}
              </Typography>
            )}
          </Box>

          {data?.capabilities && data.capabilities.length > 0 && (
            <Box>
              <Typography variant="eyebrow" sx={{ color: 'text.secondary', display: 'block', mb: 0.5 }}>
                Permisos
              </Typography>
              <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap>
                {data.capabilities.map((c) => (
                  <Chip key={c} size="small" variant="outlined" label={c} />
                ))}
              </Stack>
            </Box>
          )}

          <Divider />
          <Button
            size="small"
            color="inherit"
            startIcon={<LogOut size={14} />}
            onClick={() => {
              setAnchor(null)
              logout()
            }}
            sx={{ justifyContent: 'flex-start' }}
          >
            Cerrar sesión
          </Button>
        </Stack>
      </Menu>
    </>
  )
}
